import { AllDistances, DistanceCategoryMap } from '../utils/trainingConfig';
import apiClient from './apiClient';
import type { AthleteSettings } from './engineService';

export interface UserProps {
    user_id: string;
    name?: string;
    created_at: string;
    email: string;
    first_name: string;
    avatar_url?: string;
    role: "user" | "admin";
    latest_plan: string;
    current_plan: string;
    /** Set for a V2-enrolled athlete. The athlete screens branch on it until the cutover. */
    plan_v2_id?: string | null;
    plan_start_date: string;
    competition_date: string | null;
    stripe_customer_id?: string;
    rpe_swimming?: boolean;
    rpe_cycling?: boolean;
    rpe_running?: boolean;
    isBeta?: boolean;
    gender?: string;
    date_of_birth?: string;
    onboarding_step?: string | null;
    onboarding_actions?: { add_shortcut?: boolean } | null;
    trial_end_date?: string | null;
    payment_failed?: boolean;
}

export const getUserData = async () => {
    // Let the original axios error propagate (it carries `response.status`) so callers can
    // distinguish a transient 401 during first-login provisioning from a real failure.
    const response = await apiClient.get("users");
    return response.data as UserProps;
};

interface UpdatePasswordProps {
  oldPassword: string | null,
  newPassword: string | null,
};

export const updatePassword = async ({ oldPassword, newPassword }: UpdatePasswordProps) => {
    try {
      const response = await apiClient.post("users/update-password", { oldPassword, newPassword } );
      const data = response.data
      return data;
    } catch {
      throw new Error(`Echec lors de la mise à jour du mot de passe, vérifier les champs. Si le problème persiste, contactez le support.`);
    }
};

export const updateName = async (name: string) => {
  try {
    const response = await apiClient.post("users/update-name", { name: name } );
    return response.data;
  } catch {
    throw new Error('Impossible de mettre à jour le nom de l\'utilisateur. Si le problème persiste, contactez le support.');
  }
}

type UploadProfilePictureResponse = {
  success: boolean;
  message: string;
  imageUrl?: string;
};

export const uploadProfilePicture = async (file: File): Promise<UploadProfilePictureResponse> => {
  try {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await apiClient.post("avatars", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (error) {
    throw new Error(`Profile picture upload failed: ${error}`);
  }
}

export const resetPassword = async (userToken: string, password: string) => {
  try {
    const response = await apiClient.post("public/reset-password", { userToken, password });
    const data = response.data;
    return data;
  } catch (error) {
    throw new Error(`Data fetch failed: ${error}`);
  }
}

/**
 * PRD 6 §7 — the one write of the whole onboarding.
 *
 * `plan_v2_id` is what makes the server take the V2 branch of `update-plan` and run `enrollUserV2`.
 * `settings` stays camelCase inside the object; the envelope is snake_case, which is what
 * `enrollV2Input` expects.
 */
export interface EnrollV2Body {
    plan_v2_id: string;
    competition_date?: string;
    settings?: AthleteSettings;
}

export const enrollV2 = async (body: EnrollV2Body) => {
    const response = await apiClient.put("users/update-plan", body);
    return response.data;
}

export const abandonPlan = async () => {
  try {
    const response = await apiClient.put("users/update-plan", { current_plan: null });
    const data = response.data as UserProps;
    return data;
  } catch (error) {
    throw new Error(`Data fetch failed: ${error}`);
  }
}

export const deleteAccount = async () => {
  try {
    const response = await apiClient.delete("users");
    const data = response.data as UserProps;
    return data;
  } catch (error) {
    throw new Error(`Data fetch failed: ${error}`);
  }
}

export const updateEmail = async (newEmail: string) => {
  try {
    const response = await apiClient.put("users", { email: newEmail } );
    const data = response.data
    return data;
  } catch (error) {
    throw new Error(`Email update failed: ${error}`);
  }
}

export const selectPlan = async (
  plan_id: string,
  selectedDistance: string | null,
  competition_date: Date | null,
  isBackup?: boolean,
  weekCounts?: number,
  keywords?: string[]
) => {
  try {
    const isBeginner = keywords?.some(k => k.toUpperCase() === 'BEGINNER' || k.toUpperCase() === 'DEBUTANT');

    if (!weekCounts || weekCounts < 1) {
      throw new Error(`Invalid plan: must have at least 1 week (got ${weekCounts})`);
    }

    const mode = isBeginner ? 'beginner' : isBackup ? 'background' : 'race';
    const competitionDate = competition_date !== null ? new Date(competition_date) : null;

    const updates = {
      current_plan: plan_id,
      mode,
      competition_date: competitionDate,
      latest_plan: DistanceCategoryMap[selectedDistance as AllDistances],
    };

    const response = await apiClient.put("users/update-plan", updates);
    return response.data;
  } catch (error) {
    throw new Error(`Plan update failed: ${error}`);
  }
}

export const updateOnboardingStep = async (step: string): Promise<void> => {
  await apiClient.put("users", { onboarding_step: step });
};

export const updateOnboardingActions = async (actions: { add_shortcut?: boolean }): Promise<UserProps> => {
  const response = await apiClient.patch("users/onboarding-actions", actions);
  return response.data;
};

export const checkExistingSubscription = async (): Promise<{ skipCheckout: boolean }> => {
  const response = await apiClient.get('stripe/check-existing-subscription');
  return response.data;
};

export const createSubscriptionCheckoutLink = async (
  priceId: string,
  planType?: 'monthly' | 'quarterly' | 'yearly'
): Promise<{ url: string | null; alreadySubscribed?: boolean }> => {
  try {
    const affonsoReferral = document.cookie
      .split('; ')
      .find(c => c.startsWith('affonso_referral='))
      ?.split('=')[1] ?? '';

    const response = await apiClient.post("stripe/create-subscription-checkout", { priceId, planType, affonsoReferral });
    const { url, alreadySubscribed } = response.data;
    return { url: url ?? null, alreadySubscribed: !!alreadySubscribed };
  } catch (error) {
    throw new Error(`Failed to create Stripe checkout link: ${error}`);
  }
};

export const createCustomerPortalLink = async (): Promise<string> => {
  try {
    const response = await apiClient.get("stripe/customer-portal");
    const { url } = response.data;
    return url;
  } catch (error) {
    throw new Error(`Failed to get Stripe customer portal link: ${error}`);
  }
};

export const getCustomerSubscriptions = async (): Promise<[]> => {
  const response = await apiClient.get('stripe/subscriptions');
  return response.data;
};

export const getPaymentFailedInvoiceUrl = async (): Promise<string | null> => {
  const response = await apiClient.get('stripe/payment-failed-invoice');
  return response.data.url ?? null;
};

export const fetchChurnHash = async (): Promise<{ hash: string; subscriptionId: string }> => {
  const response = await apiClient.get('stripe/churn-hash');
  return response.data;
};

export const toggleSportRpe = async (sport: 'swimming' | 'cycling' | 'running', state: boolean, userId: string, admin: boolean = false) => {
  const key = `rpe_${sport}`;
  const route = admin ? 'admin/users' : 'users';
  const payload = admin ? { userId, updates: { [key]: state } } : { [key]: state };
  const response = await apiClient.put(route, payload);
  return response.data;
}

export const updateUserRpe = async (rpe: { rpe_swimming: boolean; rpe_cycling: boolean; rpe_running: boolean }) => {
  const response = await apiClient.put('users', rpe);
  return response.data;
}
